import { Capacitor } from '@capacitor/core'
import { CapacitorUpdater } from '@capgo/capacitor-updater'
import * as Sentry from '@sentry/capacitor'
import * as SentryReact from '@sentry/react'
import type { ErrorEvent, EventHint } from '@sentry/react'
import {
  type CombinedError,
  type Operation,
  getOperationName,
} from '@urql/core'
import {
  BUILD_VERSION,
  CAPACITOR_UPDATER_ENABLED,
  CAPGO_CHANNEL,
  ENV_IS_PRODUCTION,
  PRODUCTION_WEB_HOST,
  SENTRY_DSN,
  SENTRY_ENABLED,
  SENTRY_RELEASE,
} from '../data/environment'
import { getIsOnline } from './networkStatus'

/**
 * Error reporting: JS errors and native crashes go to the Sentry project
 * "crew-app". Mirrors customer-app-v2, plus Capgo tags: after an OTA the JS
 * bundle differs from the native build, so `release`/`dist` (package version
 * and build SHA of the JS) are tagged with the channel and running bundle.
 *
 * Swallowed errors are reported too: the app convention is `catch` +
 * `console.error`, and the console integration forwards those. Routine
 * transport failures of an offline-first field app are not errors worth an
 * event, so `shouldReport` drops them before they leave the device.
 */

// Messages the WebView/browser fetch produces for a transport failure. A
// missing lazy chunk starts the same way but is a broken bundle, and Safari's
// "Load failed" must not swallow a photo "upload failed".
const NETWORK_FAILURE =
  /failed to fetch(?! dynamically imported module)|networkerror|\bload failed|network request failed/i

/**
 * Only production builds report, so the Sentry environment separates where
 * the build runs: the production Capgo channel or the production web host
 * are `production`; preview channels, test channels and preview web deploys
 * are `preview`; anything that is not a production build is `dev`.
 */
export const resolveSentryEnvironment = ({
  isProductionBuild,
  channel,
  hostname,
}: {
  isProductionBuild: boolean
  channel: string | undefined
  hostname: string
}): 'production' | 'preview' | 'dev' => {
  if (!isProductionBuild) return 'dev'
  if (channel)
    return channel.startsWith('production') ? 'production' : 'preview'
  return hostname === PRODUCTION_WEB_HOST ? 'production' : 'preview'
}

/**
 * Keeps real errors, drops the expected noise: anything console-logged while
 * the device is offline (GraphQL retries, photo queue backoff, pull-to-refresh)
 * and plain network failures at any time.
 */
export const shouldReport = (
  event: ErrorEvent,
  hint: EventHint,
  isOnline: boolean,
): boolean => {
  const isLoggedWhileOffline = event.logger === 'console' && !isOnline
  if (isLoggedWhileOffline) return false
  const original = hint.originalException
  const message =
    original instanceof Error ? original.message : String(original ?? '')
  const exceptionValue = event.exception?.values?.[0]?.value ?? ''
  const isNetworkFailure =
    NETWORK_FAILURE.test(message) ||
    NETWORK_FAILURE.test(exceptionValue) ||
    NETWORK_FAILURE.test(event.message ?? '')
  return !isNetworkFailure
}

/**
 * Every `CombinedError` is created on the same urql frame, so the default
 * grouping would pile every GraphQL error into one issue. Group by operation
 * kind + message and tag the operation. The error is deliberately reported
 * through `console.error` like every other swallowed error: the console
 * integration captures it inside this scope, the log stays visible while
 * developing, and `shouldReport` keeps filtering it as a console event.
 */
export const reportGraphQLError = (
  error: CombinedError,
  operation: Operation,
) => {
  Sentry.withScope((scope) => {
    scope.setFingerprint(['graphql', operation.kind, error.message])
    // Typed documents widen the query type; urql's helper reads the same node
    const operationName = getOperationName(
      operation.query as Parameters<typeof getOperationName>[0],
    )
    scope.setTag('graphql_operation', operationName ?? 'unknown')
    console.error('[GraphQL Error]', error)
  })
}

export const initSentry = () => {
  Sentry.init(
    {
      dsn: SENTRY_DSN,
      enabled: SENTRY_ENABLED,
      release: SENTRY_RELEASE,
      dist: BUILD_VERSION,
      environment: resolveSentryEnvironment({
        isProductionBuild: ENV_IS_PRODUCTION,
        channel: CAPGO_CHANNEL,
        hostname: window.location.hostname,
      }),
      beforeSend: (event, hint) =>
        shouldReport(event, hint, getIsOnline()) ? event : null,
      integrations: [
        SentryReact.browserTracingIntegration(),
        // No session replay: the Capacitor SDK does not support it
        SentryReact.extraErrorDataIntegration(),
        SentryReact.captureConsoleIntegration({ levels: ['error'] }),
      ],
      // Navigation/pageload transactions only; a quarter is plenty for a
      // field app that polls the time clock all shift
      tracesSampleRate: 0.25,
      // The API has no Sentry, and its gateway CORS allowlist rejects the
      // sentry-trace/baggage headers tracing would add to every web request
      tracePropagationTargets: [],
    },
    SentryReact.init,
  )

  Sentry.setTag('platform', Capacitor.getPlatform())
  Sentry.setTag('capgo_channel', CAPGO_CHANNEL ?? 'none')
  if (CAPACITOR_UPDATER_ENABLED) void tagRunningBundle()
}

/** The Capgo bundle serving this session ("builtin" = the store build's JS). */
const tagRunningBundle = async () => {
  try {
    const { bundle } = await CapacitorUpdater.current()
    Sentry.setTag('capgo_bundle', bundle.id)
    Sentry.setTag('capgo_bundle_version', bundle.version)
  } catch (error) {
    console.error('Could not read the running Capgo bundle', error)
  }
}
